<script setup lang="ts">

import CharterGuideHero from "@/pages/CharterGuide/CharterGuideHero.vue";
import {computed, nextTick, onMounted, ref} from "vue";
import {useWindowSize} from "@vueuse/core";
import {useRoute, useRouter} from "vue-router";


const activeLink = ref('');
const router = useRouter();
const route = useRoute();

function setActive(id) {
  activeLink.value = id
}


const faqs = ref([
  {
    category: "General Information",
    items: [
      {
        question: "What is a crewed yacht charter?",
        answer: "<p>A crewed yacht charter is a luxury sailing experience where the yacht comes with a professional crew—including a captain, chef, and additional staff—who manage navigation, cooking, cleaning, and onboard services.</p>",
        open: false,
      },
      {
        question: "What types of yachts are available for crewed charters in Greece?",
        answer: "<p>You can choose from sailing yachts, catamarans, motor yachts, and superyachts. All are equipped with professional crews and tailored amenities.</p>",
        open: false,
      },
      {
        question: "Is a crewed yacht charter all-inclusive?",
        answer: "<p>It depends on the yacht. Many include meals, beverages, fuel, and water toys, while others use an APA (Advance Provisioning Allowance) system to cover expenses separately.</p>",
        open: false,
      },
      {
        question: "What is included in a crewed yacht charter?",
        answer: `
        <p class="mb-2">Typically included:</p>
        <ul class="list-disc pl-6 space-y-1">
          <li>Yacht rental</li>
          <li>Professional crew</li>
          <li>Water toys and equipment</li>
          <li>Towels and linens</li>
          <li>Yacht insurance</li>
          <li>WiFi (on many yachts)</li>
        </ul>
        <p class="mt-3">Additional charges may include VAT, provisioning, fuel (if excessive), and port fees depending on the agreement.</p>
      `,
        open: false,
      },
    ]
  },
  {
    category: "Destinations and itineraries",
    items: [
      {
        question: "Where can I sail on a crewed yacht in Greece?",
        answer: `
        <p class="mb-2">Popular regions include:</p>
        <ul class="list-disc pl-6 space-y-1">
          <li><strong>Cyclades:</strong> Mykonos, Santorini, Paros, Naxos</li>
          <li><strong>Ionian Islands:</strong> Corfu, Paxos, Lefkada, Zakynthos</li>
          <li><strong>Saronic Gulf:</strong> Hydra, Poros, Aegina</li>
          <li><strong>Dodecanese:</strong> Rhodes, Kos, Symi</li>
          <li><strong>Sporades:</strong> Skiathos, Skopelos, Alonissos</li>
        </ul>
        <p class="mt-3">Each region offers unique landscapes, cultural experiences, and sailing conditions.</p>
      `,
        open: false,
      },
      {
        question: "Can I customize my itinerary?",
        answer: "<p>Yes. Before your charter, you’ll work with Cosmos Luxury Yachts and the captain to tailor your route based on your preferences, interests, and the weather.</p>",
        open: false,
      },
      {
        question: "How many hours a day do we sail?",
        answer: "<p>Most charters sail 3–5 hours daily, leaving plenty of time for swimming, relaxing, and exploring.</p>",
        open: false,
      },
      {
        question: "Can we island-hop?",
        answer: "<p>Yes, island-hopping is one of the highlights of a crewed charter in Greece. You can visit a new island every day or stay longer at your favorite destinations.</p>",
        open: false,
      },
    ]
  },
  {
    category: "Booking and pricing",
    items: [
      {
        question: "How much does a crewed yacht charter cost in Greece?",
        answer: "<p>Prices vary based on yacht type, size, season, and location. Weekly rates typically start at €10,000 for smaller crewed yachts and can exceed €200,000 for luxury superyachts.</p>",
        open: false,
      },
      {
        question: "What is the APA (Advance Provisioning Allowance)?",
        answer: "<p>The APA is typically 20–30% of the charter price and covers expenses like fuel, food, drinks, port fees, and special requests. It’s reconciled at the end of your trip with any balance refunded.</p>",
        open: false,
      },
      {
        question: "When is the best time to charter a yacht in Greece?",
        answer: `
        <p class="mb-2">The charter season runs from <strong>May to October</strong>.</p>
        <ul class="list-disc pl-6 space-y-1">
          <li><strong>June & September:</strong> Ideal weather, fewer crowds</li>
          <li><strong>July & August:</strong> Peak season with vibrant nightlife</li>
          <li><strong>May & October:</strong> Pleasant weather and more availability</li>
        </ul>`,
        open: false,
      },
      {
        question: "How far in advance should I book?",
        answer: "<p>We recommend booking at least <strong>6–12 months in advance</strong>, especially for high season or specific yacht requests.</p>",
        open: false,
      },
      {
        question: "Are there discounts for early bookings?",
        answer: "<p>Yes. Cosmos Luxury Yachts often offers early booking discounts and last-minute deals. Contact us to learn more.</p>",
        open: false,
      },
    ]
  },
  {
    category: "Life onboard",
    items: [
      {
        question: "Do I need sailing experience?",
        answer: "<p>No. Your professional crew handles all navigation and operations. Just relax and enjoy the journey.</p>",
        open: false,
      },
      {
        question: "What is the crew’s role on the yacht?",
        answer: `
      <ul class="list-disc pl-6 space-y-1">
        <li><strong>Captain:</strong> Navigates and ensures safety</li>
        <li><strong>Chef:</strong> Prepares meals based on your preferences</li>
        <li><strong>Steward/Stewardess:</strong> Serves meals, cleans cabins, and ensures comfort</li>
        <li><strong>Deckhand:</strong> Assists with docking and water activities (on larger yachts)</li>
      </ul>`,
        open: false,
      },
      {
        question: "Can the chef accommodate dietary restrictions?",
        answer: "<p>Yes. Prior to departure, you’ll fill out a preference sheet detailing dietary needs, allergies, and favorite meals.</p>",
        open: false,
      },

      {
        question: "Is tipping the crew customary?",
        answer: "<p>Yes. A tip of <strong>10–15% of the charter fee</strong> is customary if you are satisfied with the service, and is usually given at the end of the charter.",
        open: false,
      },
      {
        question: "Are children allowed on board?",
        answer: "<p>Absolutely. Many yachts are family-friendly and equipped with water toys, child safety features, and experienced crew accustomed to hosting kids.</p>",
        open: false,
      },
      {
        question: "Are pets allowed?",
        answer: "<p>Some yachts allow pets; others do not. Please ask our team in advance if you wish to bring a pet onboard.</p>",
        open: false,
      },
      {
        question: "What should I pack for my charter?",
        answer: `
      <ul class="list-disc pl-6 space-y-1">
        <li>Light, casual clothing</li>
        <li>Swimwear</li>
        <li>Sunscreen, sunglasses, and hats</li>
        <li>Deck shoes or non-marking soles</li>
        <li>A light jacket for evenings</li>
      </ul>
      <p class="mt-3">Avoid bulky luggage; use soft bags for easier storage.</p>`,
        open: false,
      },
    ]
  },
  {
    category: "Safety and connectivity",
    items: [
      {
        question: "Is it safe to charter a yacht in Greece?",
        answer: "<p>Yes, Greece is considered one of the safest sailing destinations in the world. All yachts adhere to international safety standards.</p>",
        open: false,
      },
      {
        question: "Is there WiFi onboard?",
        answer: "<p>Most crewed yachts offer WiFi, but signal strength may vary by location. Some remote islands may have limited coverage.</p>",
        open: false,
      },
      {
        question: "Will I get seasick?",
        answer: "<p>Greek waters are generally calm, especially in summer. Catamarans are more stable, reducing the likelihood of seasickness. If you're sensitive, bring motion sickness remedies.</p>",
        open: false,
      },
    ]
  },
  {
    category: "Booking with cosmos luxury yachts",
    items: [
      {
        question: "Why choose Cosmos Luxury Yachts for my charter?",
        answer: `
        <ul class="list-disc pl-6 space-y-1">
          <li>Over 35 years of industry expertise</li>
          <li>Handpicked fleet of luxury yachts</li>
          <li>Personalized itineraries and services</li>
          <li>Financial security and transparent pricing</li>
          <li>Exceptional broker support before and during your trip</li>
        </ul>`,
        open: false,
      },

      {
        question: "How do I start planning my charter?",
        answer: "<p>Simply contact us via our website or phone. Our charter specialists will guide you through selecting the perfect yacht, building your itinerary, and handling every detail for a seamless experience.</p>",
        open: false,
      },
    ]
  },
  {
    category: "Legal and contractual",
    items: [
      {
        question: "What kind of contract is used for crewed yacht charters?",
        answer: "<p>At Cosmos Luxury Yachts, we use standard <strong>MYBA (Mediterranean Yacht Brokers Association) contracts</strong> for our crewed yacht charters. The MYBA contract is the internationally recognized industry standard, ensuring full transparency, legal protection, and clarity for both the client and the yacht owner. It clearly outlines the terms of the charter, payment schedules, cancellation policies, crew responsibilities, and what is included or excluded from the price.",
        open: false,
      },
      {
        question: "Why is the MYBA contract important?",
        answer: `
        <p class="mb-2">The MYBA charter agreement protects all parties involved by offering:</p>
        <ul class="list-disc pl-6 space-y-1">
          <li>A clear legal framework</li>
          <li>Transparent financial terms</li>
          <li>Defined roles and responsibilities of the crew and charterer</li>
          <li>Clauses for unforeseen circumstances or cancellations</li>
          <li>Flexibility in itinerary changes due to weather or technical reasons</li>
        </ul>
        <p class="mt-3">Using a MYBA contract ensures a <strong>secure, professional, and trustworthy yacht charter experience</strong> with Cosmos Luxury Yachts.</p>
      `,
        open: false,
      }
    ]
  }
]);


const sectionIds = [
  'why-charter-with-us',
  'why-charter-yacht',
  'tailored-experience',
  'costs',
  'terms',
  'faqs',
];

const { width } = useWindowSize();
const isMobileSmall = computed(() => width.value < 1024);

onMounted(async () => {
  const sectionToScroll = route.query.section as string;

  await nextTick();

  if (sectionToScroll) {
    const el = document.getElementById(sectionToScroll);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            if (id && id !== activeLink.value) {
              activeLink.value = id;
              router.replace({ path: route.path, query: { ...route.query, section: id } });
            }
          }
        });
      },
      {
        threshold: isMobileSmall ? 0.1 : 0.2,
        rootMargin: isMobileSmall ? '0px 0px -30% 0px' : '25% 0px -20% 0px',
      }
  );

  sectionIds.forEach((id) => {
    const el = document.getElementById(id);
    if (el) observer.observe(el);
  });
});




const activeFaq = ref(faqs.value.map(() => []));

</script>

<template>
  <CharterGuideHero/>

  <div class="flex gap-25 container scroll-smooth">

    <!-- DESKTOP: LEFT SIDE NAVIGATION -->
    <nav class="w-48 hidden lg:block px-4 sticky top-[20%] h-[65%] overflow-y-auto mt-20">


      <ul class="space-y-8 responsive-body-xlarge font-semibold text-primary-blueGrey">
        <li :class="[activeLink === 'why-charter-with-us' ? 'border-l-4 border-primary-violet text-primary-darkBlue' : '','hover:text-primary-darkBlue','pl-4','cursor-pointer']">
          <a href="#why-charter-with-us" >Why charter with us</a>
        </li>

        <li :class="[
        activeLink === 'why-charter-yacht' ? 'border-l-4 border-primary-violet text-primary-darkBlue' : '','hover:text-primary-darkBlue','pl-4','cursor-pointer']">
          <a href="#why-charter-yacht" class="hover:text-primary-darkBlue">Why charter a yacht</a>
        </li>
        <li :class="[
        activeLink === 'tailored-experience' ? 'border-l-4 border-primary-violet text-primary-darkBlue' : '','hover:text-primary-darkBlue','pl-4','cursor-pointer']">
          <a href="#tailored-experience" class="hover:text-primary-darkBlue">Tailored experience</a>
        </li>

        <li :class="[
        activeLink === 'costs' ? 'border-l-4 border-primary-violet text-primary-darkBlue' : '','hover:text-primary-darkBlue','pl-4','cursor-pointer']">
          <a href="#costs" class="hover:text-primary-darkBlue">Yacht chartering & costs</a>
        </li>

        <li :class="[
        activeLink === 'terms' ? 'border-l-4 border-primary-violet text-primary-darkBlue' : '','hover:text-primary-darkBlue','pl-4','cursor-pointer']">
          <a href="#terms" class="hover:text-primary-darkBlue">Contract & payment terms</a>
        </li>
        <li :class="[
        activeLink === 'faqs' ? 'border-l-4 border-primary-violet text-primary-darkBlue' : '','hover:text-primary-darkBlue','pl-4','cursor-pointer']">
          <a href="#faqs" class="hover:text-primary-darkBlue">FAQs</a></li>
      </ul>
    </nav>


    <div class="flex-1 mx-auto pt-20 px-4">

      <!-- MOBILE: TOP NAVIGATION -->
      <nav class="hidden sm:block lg:hidden sticky top-[70px] w-full mb-20 z-50 bg-background-beige border px-4 py-2">
        <ul class="flex flex-wrap justify-center gap-x-4 gap-y-2 text-primary-blueGrey font-semibold responsive-body-medium">
          <li :class="[activeLink === 'why-charter-with-us' ? 'border-b-2 border-primary-violet text-primary-darkBlue' : '','hover:text-primary-darkBlue','lg:pl-4','cursor-pointer whitespace-nowrap']">
            <a href="#why-charter-with-us">Why charter with us</a>
          </li>
          <li :class="[activeLink === 'why-charter-yacht' ? 'border-b-2 border-primary-violet text-primary-darkBlue' : '','hover:text-primary-darkBlue','lg:pl-4','cursor-pointer whitespace-nowrap']">
            <a href="#why-charter-yacht" class="hover:text-primary-darkBlue">Why charter a yacht</a>
          </li>
          <li :class="[activeLink === 'tailored-experience' ? 'border-b-2 border-primary-violet text-primary-darkBlue' : '','hover:text-primary-darkBlue','lg:pl-4','cursor-pointer whitespace-nowrap']">
            <a href="#tailored-experience" class="hover:text-primary-darkBlue">Tailored experience</a>
          </li>

          <li :class="[activeLink === 'costs' ? 'border-b-2 border-primary-violet text-primary-darkBlue' : '','hover:text-primary-darkBlue','lg:pl-4','cursor-pointer whitespace-nowrap']">
            <a href="#costs" class="hover:text-primary-darkBlue">Yacht chartering & costs</a>
          </li>

          <li :class="[activeLink === 'terms' ? 'border-b-2 border-primary-violet text-primary-darkBlue' : '','hover:text-primary-darkBlue','lg:pl-4','cursor-pointer whitespace-nowrap']">
            <a href="#terms" class="hover:text-primary-darkBlue">Contract & payment terms</a>
          </li>
          <li :class="[activeLink === 'faqs' ? 'border-b-2 border-primary-violet text-primary-darkBlue' : '','hover:text-primary-darkBlue','lg:pl-4','cursor-pointer whitespace-nowrap']">
            <a href="#faqs" class="hover:text-primary-darkBlue">FAQs</a>
          </li>
        </ul>
      </nav>


      <!-- PAGE CONTENT -->
      <div class="flex flex-col gap-28 text-primary-darkBlue">
        <div class="grid gap-8 responsive-body-xlarge">
          <div class="responsive-title-1 font-bold ">Introduction</div>
          <p>
            Vacation with a luxury yacht is, for most, an unprecedented experience, and it is expected that there is a lot of uncertainty and misconception
            around the topic. We created this extensive and informative charter guide,
            aiming to answer all the questions arising, from some simple advice on what to pack on your trip to understanding charter costs and contracts.
          </p>
        </div>

        <!-- Why charter with Cosmos Luxury Yachts -->
        <div class="grid gap-8 scroll-mt-32 responsive-body-xlarge" id="why-charter-with-us">
          <div class="responsive-title-1 font-bold mt-5">Why charter with Cosmos Luxury Yachts</div>
          <p>
            In Cosmos Luxury Yachts, we have been sailing since 1987, and have evolved to become the experts on the yachting market. Our core values have remained unchanged,
            and while representing the lifestyle and modernity of luxury yachting, we have kept our traditional approach to serving our clients and providing the most remarkable yachting experience.
          </p>

          <!-- In-Depth -->
          <div class="responsive-title-3 font-bold ">In-Depth Market Knowledge</div>
          <p>
            We don't just skim the surface, we dive deep into the nuances of the yachting market. By being active in the world of yachting since 1987,
            Cosmos Luxury Yachts stands as a testament to expertise and longevity in the industry. We have navigated the globe, we have charted as far
            as the most remote, discrete corner of the earth, and we have insight into the world's most stunning destinations. Our team of experts, boasts
            an intimate understanding of how the industry operates. Whatever it is that you are seeking to explore, in Cosmos Luxury Yachts, we possess the
            insider knowledge to make your dreams come true.
          </p>

          <!-- Greek Market -->
          <div class="responsive-title-3 font-bold ">Greek Market Specialists</div>
          <p>
            Based in Greece, we are the foremost experts in the Greek yachting market. Our in-depth knowledge of the region ensures that your Greek island-hopping experience will be nothing short of extraordinary.
          </p>

          <!-- Personalized Service -->
          <div class="responsive-title-3 font-bold ">Personalized Service</div>
          <p>
            At Cosmos Luxury Yachts, we are not some faceless corporation; we're a dedicated team of professionals passionate about curating the perfect yachting experience for our clients.
            Beyond the standard chartering process, we go the extra mile to assist you in planning your dream vacation. Our team can offer invaluable advice on where to sail, what attractions
            to explore, and anything else you need to know to make your yachting adventure stand out.
          </p>

          <!-- Fleet of the finest Yachts -->
          <div class="responsive-title-3 font-bold ">Fleet of the finest Yachts</div>
          <p>
            Our handpicked fleet of Mega yachts consists of nothing but the best, most luxurious yachts. In Cosmos Luxury Yachts, we maintain close relationships with the yacht owners to
            ensure that when you charter with us, you can expect nothing less than opulence.
          </p>
        </div>

        <!-- Why charter a yacht -->
        <div class="grid gap-8 scroll-mt-32 responsive-body-xlarge" id="why-charter-yacht">
          <div class="responsive-title-1 font-bold mt-5">Why charter a yacht</div>
          <p>
            Chartering on a mega yacht is, for most, a dream come true. A mega yacht symbolizes success, status, and prestige; it is an exclusive experience, which only a very few can afford.
            But there is a reason why a mega yacht is superior to its other alternatives, and why people strive to get to the point of cruising on one.
          </p>

          <!-- Privacy and Exclusivity -->
          <div class="responsive-title-3 font-bold ">Privacy and Exclusivity</div>
          <p>
            One of the most compelling reasons to charter a mega yacht is the privacy and exclusivity it offers. Unlike hotels or cruise ships, where you share your space with countless other guests,
            a mega yacht is entirely yours to enjoy. Whether you're with a group of friends, family, or seeking a romantic escape, the yacht becomes your private oasis, ensuring that your every need is catered to without intrusion.
          </p>

          <!-- Tailored Itinerary and Freedom -->
          <div class="responsive-title-3 font-bold ">Tailored Itinerary and Freedom</div>
          <p>
            Chartering a mega yacht provides the freedom to create a customized itinerary that suits your preferences. You're not bound by the rigid schedules of cruise ships or hotel check-out times.
            Instead, you can explore secluded bays, hidden coves, and exotic destinations at your own pace. The captain and crew are at your disposal to accommodate your desires.
          </p>

          <!-- Luxurious Amenities and Services -->
          <div class="responsive-title-3 font-bold ">Luxurious Amenities and Services</div>
          <p>
            Mega yachts are renowned for their opulence and state-of-the-art amenities. They often feature spacious cabins, private jacuzzis, gourmet kitchens, and entertainment systems, rivalling the comfort and luxury of high-end hotels.
            Moreover, you'll have a dedicated crew at your beck and call, including a chef to craft exquisite meals tailored to your tastes.
          </p>

          <!-- Fleet of the finest Yachts -->
          <div class="responsive-title-3 font-bold ">Fleet of the finest Yachts</div>
          <p>
            Our handpicked fleet of Mega yachts consists of nothing but the best, most luxurious yachts. In Cosmos Luxury Yachts, we maintain close relationships with the
            yacht owners to ensure that when you charter with us, you can expect nothing less than opulence.
          </p>

          <!-- Intimate Bonding Experience -->
          <div class="responsive-title-3 font-bold ">Fleet of the finest Yachts</div>
          <p>
            Chartering a mega yacht fosters intimacy and bonding among guests like no other vacation. With your loved ones or a select group of friends, you can create lasting memories in a private, serene environment.
            It's an opportunity to disconnect from the distractions of daily life and reconnect with those closest to you.
          </p>

        </div>


        <!-- Tailored experience -->
        <div class="grid gap-8 scroll-mt-32 responsive-body-xlarge" id="tailored-experience">
          <div class="responsive-title-1 font-bold mt-5">Tailored experience</div>
          <p>
            A luxury yacht charter offers a meticulously tailored experience, designed to cater to the unique preferences and desires of each guest on board. Central to this personalized service is the preference sheet,
            a document that plays a crucial role in ensuring that every detail of the charter aligns with the guests' wishes. The preference sheet is typically sent to guests before the charter begins.
            It's a comprehensive questionnaire that covers various aspects of the trip.
          </p>

          <!-- Personalized Culinary Experience -->
          <div class="responsive-title-3 font-bold ">Personalized Culinary Experience</div>
          <p>
            It inquires about dietary restrictions, allergies, and food preferences, enabling the onboard chef to create customized menus. The yacht's chef uses the information from the preference
            sheet to craft a tailored culinary experience. This includes designing menus to accommodate dietary needs and preferences, ensuring that every meal is a delight.
          </p>

          <!-- Customized Itinerary -->
          <div class="responsive-title-3 font-bold ">Customized Itinerary</div>
          <p>
            The charter's itinerary is shaped around the guests' desires. Guests can specify their preferred activities and excursions, such as water sports, diving, fishing, or cultural tours
            and the crew will plan accordingly. It also gathers information about sleeping arrangements, cabin preferences, and any special requests, like spa treatments or particular brands of toiletries.
          </p>
        </div>


        <!-- Yacht Chartering & Costs -->
        <div class="grid gap-8 scroll-mt-32 responsive-body-xlarge" id="costs">
          <div class="responsive-title-1 font-bold mt-5">All the important components of yacht chartering & costs</div>

          <!-- Low and high season -->
          <div class="responsive-title-3 font-bold ">Low and high season</div>
          <p>
            The charter fee for the same yacht could vary depending on the time of the year that it is chartered. Charter rates during the high season are typically more
            expensive due to increased demand. This also means that the costs for both the yacht charter and other related expenses, such as provisions and marina fees, will be elevated.
            On the other side, yacht charter rates are typically lower during the low season. The low and high season dates depend on the location of the yacht. For example, it is likely
            for a yacht in the Caribbean during the winter season to be high season, while it is the summertime in the Mediterranean.
          </p>

          <!-- Base charter fee -->
          <div class="responsive-title-3 font-bold ">Base charter fee</div>
          <p>
            The base charter fee is the fundamental cost of the yacht for the specific period rented. It covers the basic expenses associated with the
            yacht charter and includes the use of the yacht, its amenities, and usually also the crew’s services but does not include additional expenses such as fuel,
            provisions, crew wages, and other incidentals. All those additional expenses that vary from charter to charterer, are covered by the APA.
          </p>

          <!-- APA -->
          <div class="responsive-title-3 font-bold ">APA</div>
          <p>
            APA stands for "Advance Provisioning Allowance." The APA is a crucial aspect of the yacht charter agreement, and it represents an additional
            cost that charterers pay on top of the base charter fee. The purpose of the APA is to cover various expenses associated with the yacht charter that are not included in the base fee.
          </p>
          <p>
            The APA typically covers expenses such as, the cost of fuel, the crew’s services, food and beverages, dockage fees and communication and entertainment. Usually, t
            he APA is around 25% to 30% of the base charter fee, however this depends on the charterer’s tastes and requirements so this estimation could vary greatly.
          </p>
          <p>
            The APA is collected by the yacht charter broker before the charter begins. This could be a month before the charter or closer to the date of the charter
            if the charter was last minute. Throughout the charter, the captain and crew use the funds from the APA to pay for the expenses incurred. At the end of the
            charter, any unused funds are returned to the charterer, or if the expenses exceed the APA, the charterer may be required to cover the difference.
          </p>

          <!-- The Crew & Gratuities -->
          <div class="responsive-title-3 font-bold ">The Crew & Gratuities</div>
          <p>
            Behind the scenes of a dream vacation aboard a mega yacht are the unsung heroes who make it all possible - the dedicated crew.
          </p>
          <p>
            The charter crew are industry professionals who have been personally chosen by the owner of the yacht. The size of a mega yacht typically determines
            the number and specialization of its crew members. Typically, for a mid-size charter, the crew team ranges from 6 to 15 people. However, the crew-to-guest
            ratio is also a crucial factor in delivering optimized service. The ratio varies depending on the yacht's size and the level of service expected. Still,
            it often ranges from a 1:1 to 1:3 ratio, with larger yachts typically having more crew members to cater to a greater number of guests.
          </p>

          <!-- Captain -->
          <div class="responsive-title-3 font-bold ">CAPTAIN</div>
          <p>
            The captain is the highest-ranking crew member responsible for navigating the yacht, ensuring safety, managing the crew, and overseeing all operations. They have extensive experience and hold the necessary licenses.
          </p>

          <!-- Chef -->
          <div class="responsive-title-3 font-bold ">CHEF</div>
          <p>
            The yacht's chef is a culinary artist who designs menus, prepares gourmet meals, and caters to guests' dietary preferences. The chef on a luxury yacht is top-notch,
            as they are expected to cater to everything the charterer requests them, while some charter Chefs have been awarded Michelin stars. Prior to confirming your
            yacht charter reservation, you can communicate your specific catering preferences. This includes sharing your dietary preferences, both likes and dislikes,
            as well as any allergies that the onboard Chef should be informed about. You can make use of the preference sheet to outline what you would like to eat and drink
            and be as detailed as possible. This way, the onboard Chef can meticulously craft each meal to perfection, ensuring it aligns perfectly with your personalized preferences.
          </p>

          <!-- Stewardess -->
          <div class="responsive-title-3 font-bold ">STEWARDESS</div>
          <p>
            Stewardesses and stewards are responsible for guest comfort and service. They maintain cabins, assist with meals, and provide attentive care to ensure guests
            have a luxurious and worry-free experience. Some of them can also offer massages, manicures, and other personalized services.
          </p>

          <!-- Deckhands -->
          <div class="responsive-title-3 font-bold ">DECKHANDS</div>
          <p>
            Deckhands handle various tasks related to maintaining the exterior of the yacht. They assist with water sports activities, handle mooring and anchoring, and keep the yacht's exterior intact.
          </p>

          <!-- Engineers -->
          <div class="responsive-title-3 font-bold ">ENGINEERS</div>
          <p>
            The engineers oversee the yacht's mechanical systems, ensuring everything runs smoothly. The engineers are out of sight, as they spend their time mostly below deck, but they are vital for the safety and functionality of the vessel.
          </p>

          <!-- Tipping -->
          <div class="responsive-title-3 font-bold ">TIPPING</div>
          <p>
            Tipping is a customary practice in the mega yacht industry, reflecting gratitude for the exceptional service provided by the crew. A common guideline for tipping is around 5-20% of the total charter fee.
            This amount is typically split among the crew members based on a predetermined distribution scheme. While there are industry standards, tipping is ultimately at the discretion of the guests.
            If guests have received exceptional service, they may choose to tip more generously.
          </p>

          <!-- Insurance -->
          <div class="responsive-title-3 font-bold ">INSURANCE</div>
          <p>
            While insurance is not tied to the charter fee, it is important for the charterer to know the available types of insurance, and how they can protect themselves from unforeseen occurrences.
          </p>
          <p>
            While the yacht owner maintains insurance coverage for the yacht, the charterer is liable for any loss, damage, or liabilities, arising from any act of negligence. For that instance, the charterer is
            recommended to take a charterer’s Liability Insurance.
          </p>
          <p>
            In the case of an unexpected event or circumstance that forces you to cut short or end your trip earlier than planned, the charterer can take a Curtailment insurance. This insurance can help recover
            some of the non-refundable expenses incurred for pre-paid travel arrangements, such as flights, accommodations, and activities when you cannot complete your trip as originally intended.
          </p>

          <!-- VAT -->
          <div class="responsive-title-3 font-bold ">Value Added Tax (VAT)</div>
          <p>
            VAT is applicable to regions such as some European Union countries and other jurisdictions. The VAT charges will be mentioned in the contract, and it is usually paid on the later installment of the charter fee.
          </p>
        </div>


        <!-- Contracts & Payment terms -->
        <div class="grid gap-8 scroll-mt-32 responsive-body-xlarge" id="terms">
          <div class="responsive-title-1 font-bold mt-5">Understand Charter Contracts & Payment Terms</div>

          <!-- MYBA Charter Agreement -->
          <div class="responsive-title-3 font-bold ">MYBA Charter Agreement</div>
          <p>
            At Cosmos Luxury Yachts, we use the MYBA (Mediterranean Yacht Brokers Association) charter agreement, a widely recognized and most commonly used contract in the luxury yacht charter industry.
            It serves as a comprehensive and standardized framework for formalizing charter arrangements between charterers and yacht owners.
          </p>

          <!-- Payment Terms -->
          <div class="responsive-title-3 font-bold ">Payment Terms</div>
          <p>
            Once the terms have been agreed upon by both parties and upon signing the charter agreement, the charterer is required to make the first installment. The first installment is typically a percentage
            of the total charter fee (usually 50%) and is due shortly after the contract is signed. This deposit secures the charter dates and confirms the reservation. As the charter date approaches, the second
            installment will be due to be paid, which equals the remaining of the charter fee plus the APA, VAT, or any additional charges.
          </p>
          <p>
            The number of installments is usually 2; however, depending on the agreements of the yacht owner, the installments can be separated into 3. However, if the charter was last minute, the charterer might be required to pay all at once.
          </p>

          <!-- Security Deposit -->
          <div class="responsive-title-3 font-bold ">Security Deposit</div>
          <p>
            A security deposit is rarely required. However, if requested by the yacht owner, the security deposit is to be paid shortly before the charter begins, and it serves as collateral against potential damages to
            the yacht or its equipment during the charter. The security deposit is refundable but is subject to deductions for any damages or losses incurred during the charter.
          </p>

          <!-- Cancellation Policy -->
          <div class="responsive-title-3 font-bold ">Cancellation Policy</div>
          <p>
            If the charterer decides to cancel the charter, the owner of the yacht is entitled to keep the first installment of the charter fee, according to the MYBA contract.
            For an alternative resolution, the charterer can negotiate with the broker.
          </p>
        </div>

        <!-- FAQs -->

        <div class="grid scroll-mt-32" id="faqs">
          <div class="responsive-title-1 font-bold mb-8 mt-5">Frequently Asked Questions</div>

          <div v-for="(group, groupIndex) in faqs" :key="groupIndex" class="mb-10">
            <div class="responsive-title-3 font-semibold mt-10 mb-6 text-primary-darkBlue uppercase">{{ group.category }}</div>

            <el-collapse v-model="activeFaq[groupIndex]" accordion>
              <el-collapse-item
                  v-for="(faq, index) in group.items"
                  :key="index"
                  :name="index.toString()"
              >
                <template #title>
                  <div class="responsive-title-3 py-5 text-primary-darkBlue whitespace-normal break-words w-full">
                    <span>{{ faq.question }}</span>
                  </div>
                </template>

                <div
                    class="responsive-title-4 text-primary-darkBlue bg-background-light border-l-[12px] border-primary-violet px-4 pb-5"
                    v-html="faq.answer"
                ></div>
              </el-collapse-item>
            </el-collapse>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<style scoped>

:deep(.el-collapse-item__header) {
  height: auto !important;
  overflow: visible !important;
}

:deep(.el-collapse-item__content) {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

:deep(.el-collapse-item__header) {
  @apply font-semibold flex justify-between items-center px-4 py-3 transition-all border-t border-primary-violet bg-transparent;
}
:deep(.el-collapse) {
  @apply  border border-primary-violet;
}

:deep(.el-collapse-item__header:hover) {
  background-color: #f8f4f3;
  border-left: 12px solid #beA5aa;
}

:deep(.el-collapse-item.is-active .el-collapse-item__header) {
  background-color: #f8f4f3;
  border-left: 12px solid #beA5aa;
  border-top:none;
  border-bottom:none;
}

:deep(.el-collapse-item.is-active .el-collapse-item__arrow) {
  color: #beA5aa;
}


:deep(.el-collapse-item__header:hover .el-collapse-item__arrow) {
  @apply text-primary-violet;
}


:deep(.el-collapse-item__arrow) {
  width: 40px !important;
  height: 40px !important;
  min-width: 20px !important;
  min-height: 20px !important;
  flex-shrink: 0 !important;
  margin-left: 10px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  transform: rotate(90deg);
  transition: transform 0.3s ease;
  color: #1f184f;
}

:deep(.el-collapse-item__arrow svg) {
  width: 28px !important;
  height: 28px !important;
}


:deep(.el-collapse-item__arrow.is-active) {
  transform: rotate(270deg);
}

</style>
